import { SCORE_DECIMAL_PLACES, SCORE_MAX, SCORE_MIN } from '../calibrationConstants';

import type {
    CalibrationAdjustmentDirection,
    CalibrationEmployee,
    CalibrationReport,
} from '@/types/calibration';

/** A bulk sweep expresses its change as a percentage out of one hundred, matching the backend's
 * `CalibrationAdjustmentService.PercentageBase`. */
const PERCENTAGE_BASE = 100;

export interface ComputeBulkPreviewOptions {
    report: CalibrationReport;
    /** Managers to target; empty means every manager currently on the report. */
    managerIds: string[];
    direction: CalibrationAdjustmentDirection;
    percentage: number;
}

export interface BulkPreviewResult {
    affectedCount: number;
    updatedScoresByEmployeeId: Record<string, number>;
}

/**
 * The score a bulk sweep multiplies: the adjustment's `calibratedScore`, draft or published, else
 * the submitted `score`. A row reopened as a draft with a comment and no new score reports a
 * `null` `calibratedScore` while the backend still compounds from its published snapshot, which
 * the report never exposes; the preview falls back to the submitted score there, and `POST …/Bulk`
 * returns the authoritative value.
 */
const currentScoreOf = (employee: CalibrationEmployee): number =>
    employee.adjustment?.calibratedScore ?? employee.score;

interface ApplyPercentageOptions {
    score: number;
    direction: CalibrationAdjustmentDirection;
    percentage: number;
}

const applyPercentage = ({ score, direction, percentage }: ApplyPercentageOptions): number => {
    const signedPercentage = direction === 'Increase' ? percentage : -percentage;
    const adjusted = score * (1 + signedPercentage / PERCENTAGE_BASE);
    const clamped = Math.min(SCORE_MAX, Math.max(SCORE_MIN, adjusted));

    const roundingFactor = 10 ** SCORE_DECIMAL_PLACES;
    return Math.round(clamped * roundingFactor) / roundingFactor;
};

/**
 * Client-side preview of a bulk calibration sweep, for the confirmation modal's "N people
 * affected" summary. Mirrors `CalibrationAdjustmentService.BulkAdjustAsync` on the backend
 * (percentage applied to the current score, clamped to 0-100, rounded to one decimal) so the
 * preview matches what `POST …/Bulk` will actually persist; the request itself is the source of
 * truth, this is only ever shown before it is sent.
 */
export const computeBulkPreview = ({
    report,
    managerIds,
    direction,
    percentage,
}: ComputeBulkPreviewOptions): BulkPreviewResult => {
    const targetsEveryManager = managerIds.length === 0;
    const targetedManagerIds = new Set(managerIds);

    const targetedManagers = report.managers.filter(
        (manager) => targetsEveryManager || targetedManagerIds.has(manager.managerId),
    );

    const updatedScoresByEmployeeId: Record<string, number> = {};

    targetedManagers.forEach((manager) => {
        manager.employees.forEach((employee) => {
            updatedScoresByEmployeeId[employee.employeeId] = applyPercentage({
                score: currentScoreOf(employee),
                direction,
                percentage,
            });
        });
    });

    return {
        affectedCount: Object.keys(updatedScoresByEmployeeId).length,
        updatedScoresByEmployeeId,
    };
};
